<!-- Spending breakdowns as horizontal bars, one panel per breakdown. -->
<script lang="ts">
  import type { Breakdown } from "@split/core";
  import { compactMicro } from "../lib/format.ts";
  import MultiLineChart, { type LineSeries } from "./charts/MultiLineChart.svelte";

  export interface Section {
    title: string;
    rows: Breakdown[];
    /** Bar color for a row (defaults to ink). */
    color?: (label: string) => string;
    /** Display text for a row's label. */
    label?: (label: string) => string;
    /** The same breakdown month by month, for the Graph switch. */
    graph?: { labels: string[]; series: LineSeries[] };
  }

  let { sections }: { sections: Section[] } = $props();
  let graphOn = $state<Record<string, boolean>>({});
</script>

<div class="grid">
  {#each sections as s (s.title)}
    {@const max = Math.max(1, ...s.rows.map((r) => r.totalMicro))}
    <section class="panel">
      <div class="ph">
        <h2>{s.title}</h2>
        {#if s.graph}
          <span class="seg" role="group" aria-label="Show {s.title} as">
            <button aria-pressed={!graphOn[s.title]} onclick={() => (graphOn[s.title] = false)}>List</button>
            <button aria-pressed={!!graphOn[s.title]} onclick={() => (graphOn[s.title] = true)}>Graph</button>
          </span>
        {/if}
      </div>
      {#if s.graph && graphOn[s.title]}
        <MultiLineChart title="{s.title}, running total" labels={s.graph.labels} series={s.graph.series} height={170} />
      {:else if s.rows.length === 0}
        <p class="muted">Nothing yet.</p>
      {:else}
        <ul>
          {#each s.rows as r, ri (ri)}
            <li>
              <span class="label">{s.label ? s.label(r.label) : r.label}</span>
              <span class="track" aria-hidden="true">
                <span class="fill" style:width="{(r.totalMicro / max) * 100}%" style:background={s.color ? s.color(r.label) : "var(--ink-2)"}></span>
              </span>
              <span class="num v">{compactMicro(r.totalMicro)}</span>
            </li>
          {/each}
        </ul>
      {/if}
    </section>
  {/each}
</div>

<style>
  .ph {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    min-height: 34px;
    margin-bottom: 14px;
  }

  .seg {
    display: inline-flex;
    flex: none;
    gap: 2px;
    padding: 3px;
    border-radius: 999px;
    background: var(--wash);
  }

  .seg button {
    min-height: 28px;
    padding: 3px 12px;
    border: 0;
    border-radius: 999px;
    background: none;
    font: inherit;
    font-size: var(--t-xs);
    color: var(--muted);
    cursor: pointer;
  }

  .seg button[aria-pressed="true"] {
    background: var(--surface);
    color: var(--ink);
    font-weight: 650;
    box-shadow: 0 1px 2px rgb(0 0 0 / 0.12);
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 16px;
  }

  .panel {
    background: var(--surface);
    border: 1px solid var(--rule);
    border-radius: var(--r-lg);
    padding: 16px 18px;
  }

  .panel h2 {
    font-size: var(--t-md);
    margin: 0;
  }

  ul {
    margin: 0;
    padding: 0;
    list-style: none;
    display: grid;
    gap: 9px;
  }

  li {
    display: grid;
    grid-template-columns: minmax(80px, 34%) 1fr auto;
    gap: 10px;
    align-items: center;
    font-size: var(--t-sm);
  }

  .label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .track {
    height: 10px;
    border-radius: 5px;
    background: var(--wash);
    overflow: hidden;
  }

  .fill {
    display: block;
    height: 100%;
    border-radius: 5px;
  }

  .v {
    font-weight: 600;
    min-width: 64px;
    text-align: right;
  }
</style>
